import { getProductItemPriceInfo } from '@croquiscom/zigzag-server-common/lib/order_utils';
import groupBy from 'lodash-es/groupBy';
import minBy from 'lodash-es/minBy';
import { CartItem, CartShop, ShippingGroup } from '../store';
import { CouponProductInfo, ShippingGroupProductShippingFeeType, ShippingType } from 'api2/types';
import { getZigzinShopId } from 'util/zigzin';

export const is_within_3_hour = (date_last_added: number) => {
  return (Date.now() - date_last_added) / (60 * 60 * 1000) < 3;
};

export const getInitialOrderable = (cart_shop_list: CartShop[]): { [product_item_id: string]: boolean } => {
  const selected: { [product_item_id: string]: boolean } = {};

  for (const cart_shop of cart_shop_list) {
    for (const cart_item of cart_shop.shipping_group_list.flatMap((group) => group.cart_item_list)) {
      if (cart_item.is_orderable) {
        selected[`${cart_item.product_item_id}${cart_item.cart_item_hash}`] = true;
      }
    }
  }

  return selected;
};

export const getInitialSelected = (cart_shop_list: CartShop[]): { [product_item_id: string]: boolean } => {
  const selected: { [product_item_id: string]: boolean } = {};

  for (const cart_shop of cart_shop_list) {
    for (const cart_item of cart_shop.shipping_group_list.flatMap((group) => group.cart_item_list)) {
      if (cart_item.is_orderable) {
        selected[`${cart_item.product_item_id}${cart_item.cart_item_hash}`] = true;
      } else {
        selected[`${cart_item.product_item_id}${cart_item.cart_item_hash}`] = false;
      }
    }
  }

  return selected;
};

// 옵션 이름민 필터 조회
export const getOptionNames = ({ additional_option_detail_list, product_item }: CartItem) => {
  additional_option_detail_list = additional_option_detail_list || [];
  if (!additional_option_detail_list.length) {
    return product_item.option_list.map(({ value }) => value).join('/');
  }
  const {
    product: { option_list },
  } = product_item;
  return product_item.option_list
    .map(({ value }) => value)
    .concat(
      additional_option_detail_list.map(({ option_code, value, value_code }) => {
        if (option_code && value_code) {
          const matchedOption = option_list.find((o) => o.code === option_code);
          if (matchedOption) {
            const matchedValueSet = matchedOption.value_set.find((v) => v.code === value_code);
            if (matchedValueSet) {
              return matchedValueSet.name;
            }
          }
          return '';
        } else {
          return value;
        }
      }),
    )
    .filter(Boolean)
    .join('/');
};

export const getShippingFeeToPay = (
  shipping_group_list: ShippingGroup[],
  selected_item_id_list: {
    [item_identifier: string]: boolean;
  },
) => {
  return shipping_group_list
    .map((shipping_group) => {
      const shop_checked_item_list = getCartItemListCheckedItems(shipping_group.cart_item_list, selected_item_id_list);
      const shipping_fee_type = shop_checked_item_list.resolved_shipping_fee_info.fee_type;
      const base_fee = shop_checked_item_list.resolved_shipping_fee_info.base_fee || 0;
      switch (shipping_fee_type) {
        case ShippingGroupProductShippingFeeType.FREE: {
          return { amount: 0, amount_with_currency: 0 };
        }
        case ShippingGroupProductShippingFeeType.CONDITIONAL_FREE: {
          const conditional_amount = shop_checked_item_list.resolved_shipping_fee_info.conditional_amount || 0;
          if (shop_checked_item_list.amount - shop_checked_item_list.promotion_discount_amount >= conditional_amount) {
            return { amount: 0 };
          }
          return { amount: base_fee };
        }
        case ShippingGroupProductShippingFeeType.CHARGED: {
          return { amount: base_fee };
        }
        default: {
          return { amount: 0 };
        }
      }
    })
    .reduce(
      (result, { amount }) => {
        result.amount += amount;
        return result;
      },
      {
        amount: 0,
      },
    );
};

export const getItemList = (
  shop: CartShop['shop'],
  shipping_group_list: CartShop['shipping_group_list'],
  selected_item_ids: { [item_identifier: string]: boolean },
  is_zigzin: boolean,
) => {
  return shipping_group_list
    .filter((group) =>
      is_zigzin
        ? group.shipping_info.shipping_type === ShippingType.ZIGZIN
        : group.shipping_info.shipping_type !== ShippingType.ZIGZIN,
    )
    .flatMap((group) => group.cart_item_list)
    .filter(({ product_item_id, cart_item_hash }) => selected_item_ids[`${product_item_id}${cart_item_hash}`])
    .map<CouponProductInfo>((cart_item) => ({
      product_id: cart_item.product_item.product.id,
      product_item_id: cart_item.product_item_id,
      shop_id: is_zigzin ? getZigzinShopId() : shop.id,
      amount: getProductItemPriceInfo(cart_item.product_item, cart_item.additional_option_detail_list || []).price,
      quantity: cart_item.quantity,
      origin_shop_id: cart_item.origin_shop?.id ?? shop.id,
    }));
};

export const getShopCheckedItems = (
  cart_shop: CartShop,
  selected_item_ids: {
    [item_identifier: string]: boolean;
  },
) => {
  return cart_shop.shipping_group_list.reduce<{
    amount: number;
    count: number;
  }>(
    (result, group) => {
      const { amount, count } = getCartItemListCheckedItems(group.cart_item_list, selected_item_ids);
      result.amount += amount;
      result.count += count;
      return result;
    },
    { amount: 0, count: 0 },
  );
};

export const getCartItemListCheckedItems = (
  cart_item_list: CartItem[],
  selected_item_ids: {
    [item_identifier: string]: boolean;
  },
) => {
  const shipping_group_checked_items = cart_item_list.reduce<{
    amount: number;
    count: number;
    promotion_discount_amount: number;
    resolved_shipping_fee_info: {
      fee_type: ShippingGroupProductShippingFeeType;
      base_fee: number;
      conditional_amount: number;
    };
  }>(
    (result, { product_item, quantity, product_item_id, cart_item_hash, additional_option_detail_list }) => {
      const { price } = getProductItemPriceInfo(product_item, additional_option_detail_list || []);
      const item_identifier = `${product_item_id}${cart_item_hash}`;
      const amount = selected_item_ids[item_identifier] ? price * quantity : 0;
      result.amount += amount;
      if (selected_item_ids[item_identifier]) {
        result.count++;
      }

      return result;
    },
    {
      amount: 0,
      count: 0,
      promotion_discount_amount: 0,
      resolved_shipping_fee_info: {
        fee_type: ShippingGroupProductShippingFeeType.FREE,
        base_fee: 0,
        conditional_amount: 0,
      },
    },
  );

  // 상품별 배송비 설정
  shipping_group_checked_items.resolved_shipping_fee_info = getResolvedShippingFeeInfo(
    cart_item_list,
    selected_item_ids,
  );
  return shipping_group_checked_items;
};

export interface ResolvedShippingFeeInfo {
  fee_type: ShippingGroupProductShippingFeeType;
  base_fee: number;
  conditional_amount: number;
}

export const getResolvedShippingFeeInfo = (
  cart_item_list: CartItem[],
  selected_item_ids: {
    [item_identifier: string]: boolean;
  },
): ResolvedShippingFeeInfo => {
  const selected_cart_item_list = cart_item_list.filter((cart_item) => {
    const item_identifier = `${cart_item.product_item_id}${cart_item.cart_item_hash}`;
    return selected_item_ids[item_identifier];
  });
  const shiping_fee_list = selected_cart_item_list.map((cart_item) => cart_item.product_item.product.shipping_fee);
  const grouped_shipping_fees_by_type = groupBy(shiping_fee_list, 'fee_type');

  // 배송비 무료
  const free_shipping_fee_list = grouped_shipping_fees_by_type[ShippingGroupProductShippingFeeType.FREE];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (free_shipping_fee_list && free_shipping_fee_list.length > 0) {
    return { fee_type: ShippingGroupProductShippingFeeType.FREE, base_fee: 0, conditional_amount: 0 };
  }

  // 조건부 배송비
  const conditional_shipping_fee_list =
    grouped_shipping_fees_by_type[ShippingGroupProductShippingFeeType.CONDITIONAL_FREE];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (conditional_shipping_fee_list && conditional_shipping_fee_list.length > 0) {
    const minShippingFee = minBy(conditional_shipping_fee_list, 'conditional_amount');
    return {
      fee_type: ShippingGroupProductShippingFeeType.CONDITIONAL_FREE,
      base_fee: minShippingFee?.base_fee || 0,
      conditional_amount: minShippingFee?.conditional_amount || 0,
    };
  }

  // 기본 배송비
  const charged_shipping_fee_list = grouped_shipping_fees_by_type[ShippingGroupProductShippingFeeType.CHARGED];
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (charged_shipping_fee_list && charged_shipping_fee_list.length > 0) {
    const minShippingFee = minBy(charged_shipping_fee_list, 'base_fee');

    return {
      fee_type: ShippingGroupProductShippingFeeType.CHARGED,
      base_fee: minShippingFee?.base_fee || 0,
      conditional_amount: 0,
    };
  }

  // 기본
  return { fee_type: ShippingGroupProductShippingFeeType.FREE, base_fee: 0, conditional_amount: 0 };
};

export const byDateLastAdded = (shop1: CartShop, shop2: CartShop) => {
  return (
    Math.max(...shop2.shipping_group_list.flatMap((group) => group.cart_item_list).map((m) => m.date_last_added)) -
    Math.max(...shop1.shipping_group_list.flatMap((group) => group.cart_item_list).map((m) => m.date_last_added))
  );
};
